<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-backdrop" @click="close">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">Adjust Order: {{ order.orderNumber }}</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close">
            <font-awesome-icon icon="fa-solid fa-xmark"/>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="!adjustments" class="text-center p-3">
            <p>Loading items...</p>
          </div>
          <div v-else class="adjust-form">
            <div v-for="(item, index) in adjustments" :key="item.itemId" class="adjust-item">
              <p class="item-name">{{ item.productName }} (Current Qty: {{ item.originalQuantity }})</p>
              <div class="item-controls">
                <select v-model="item.action" class="form-select" @change="onActionChange(item)">
                  <option value="ACCEPT">Accept</option>
                  <option value="ADJUST_QUANTITY">Adjust Quantity</option>
                  <option value="REJECT">Reject</option>
                </select>
                <input
                  v-if="item.action === 'ADJUST_QUANTITY'"
                  v-model.number="item.newQuantity"
                  type="number"
                  class="form-control"
                  step="0.5"
                  min="0"
                >
                <input
                  v-model.trim="item.itemReason"
                  type="text"
                  class="form-control item-reason"
                  placeholder="Item-specific reason (optional)"
                >
              </div>
              <div class="predefined-reasons">
                <button
                  v-for="reason in predefinedItemReasons"
                  :key="reason"
                  class="btn-reason"
                  @click.prevent="applyItemReason(item, reason)"
                >
                  {{ reason }}
                </button>
              </div>
            </div>
            <hr>
            <div class="form-group">
              <label for="overall-reason">Overall Reason for Adjustments</label>
              <textarea
                v-model.trim="overallReason"
                id="overall-reason"
                class="form-control"
                placeholder="Optional, e.g., Some items were out of stock"
              ></textarea>
              <div class="predefined-reasons">
                <button
                  v-for="reason in predefinedOverallReasons"
                  :key="reason"
                  class="btn-reason"
                  @click.prevent="applyOverallReason(reason)"
                >
                  {{ reason }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close" :disabled="submitting">Cancel</button>
          <button type="button" class="btn btn-primary" @click="submit" :disabled="!isFormValid || submitting">
            <font-awesome-icon v-if="submitting" icon="fa-solid fa-spinner" spin />
            <span>{{ submitting ? 'Submitting...' : 'Submit Adjustments' }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AdjustOrderModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      adjustments: null,
      overallReason: '',
      predefinedItemReasons: [
        'Item out of stock',
        'Partial quantity available',
        'Customer requested change',
      ],
      predefinedOverallReasons: [
        'Some items unavailable',
        'Customer updated order',
        'Store preparing order',
      ]
    };
  },
  watch: {
    order: {
      immediate: true,
      handler(newOrder) {
        if (newOrder && newOrder.items) {
          this.initializeAdjustments(newOrder.items);
        } else {
          this.adjustments = null;
        }
      }
    }
  },
  computed: {
    isFormValid() {
      if (!this.adjustments) return false;
      return this.adjustments.every(item => {
        if (item.action === 'ADJUST_QUANTITY') {
          return typeof item.newQuantity === 'number' && item.newQuantity >= 0;
        }
        return true;
      });
    }
  },
  methods: {
    initializeAdjustments(items) {
      this.adjustments = items.map(item => ({
        itemId: item.id,
        productName: item.productName,
        originalQuantity: item.quantity,
        action: 'ACCEPT', // Default action
        newQuantity: item.quantity,
        itemReason: ''
      }));
      this.overallReason = '';
    },
    onActionChange(item) {
      if (item.action !== 'ADJUST_QUANTITY') {
        item.newQuantity = item.originalQuantity; // Reset if not adjusting
      }
    },
    applyItemReason(item, reason) {
      item.itemReason = reason;
    },
    applyOverallReason(reason) {
      this.overallReason = reason;
    },
    close() {
      if (this.submitting) return;
      this.$emit('close');
    },
    submit() {
      if (this.submitting) return;
      if (!this.isFormValid) {
        // This should ideally not be possible if button is disabled, but as a safeguard:
        Swal.fire('Invalid Input', 'Please check the quantities you have entered.', 'warning');
        return;
      }
      
      const payload = {
        items: this.adjustments.map(({ itemId, action, newQuantity, itemReason }) => {
          const itemPayload = { itemId, action };
          if (action === 'ADJUST_QUANTITY') {
            itemPayload.newQuantity = newQuantity;
          }
          if (itemReason) {
            itemPayload.itemReason = itemReason;
          }
          return itemPayload;
        }),
        reason: this.overallReason || 'Adjusted via POS'
      };
      
      this.$emit('adjust', payload);
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75); /* Darker backdrop */
  backdrop-filter: blur(5px); /* Stronger blur */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
}

.modal {
  background-color: var(--card-bg);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  width: 90vw;
  max-width: 600px;
  max-height: 90vh;
  border-radius: 16px; /* Slightly larger radius */
  border: 1px solid rgba(255, 255, 255, 0.1); /* Subtle border for definition */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5); /* Stronger shadow */
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: var(--text-primary);
  cursor: pointer;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  color: var(--text-primary, #f3f4f6);
}

.adjust-item {
  background: var(--secondary-button-bg);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid var(--card-border);
}

.item-name {
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-primary, #f3f4f6);
}

.item-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.item-reason {
  grid-column: 1 / -1;
}

.predefined-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn-reason {
  background-color: var(--bg-primary);
  border: 1px solid var(--card-border);
  color: var(--text-secondary);
  border-radius: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-reason:hover {
  background-color: var(--accent-electric-blue);
  color: white;
  border-color: var(--accent-electric-blue);
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--card-border);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Standard form-control styles if not globally available */
.form-control, .form-select {
  display: block;
  width: 100%;
  padding: .375rem .75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--text-primary, #f3f4f6);
  background-color: var(--bg-primary, #1f2937);
  background-clip: padding-box;
  border: 1px solid var(--card-border);
  border-radius: .25rem;
  transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
}

.form-control::placeholder {
  color: var(--text-secondary, #9ca3af);
  opacity: 1;
}

.form-select option {
  color: var(--text-primary, #f3f4f6);
  background-color: var(--card-bg, #111827);
}

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .modal-backdrop {
    align-items: flex-start;
    padding: 0.75rem;
    overflow-y: auto;
  }

  .modal {
    width: 100%;
    max-width: none;
    max-height: calc(100vh - 1.5rem);
    margin: auto 0;
    border-radius: 14px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .modal-header {
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }

  .modal-title {
    font-size: 1rem;
    line-height: 1.3;
    padding-right: 0.5rem;
  }

  .modal-body {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .item-controls {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .modal-footer .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .modal-backdrop {
    padding: 0.5rem;
  }

  .modal {
    max-height: calc(100vh - 1rem);
    border-radius: 12px;
  }

  .adjust-item {
    padding: 0.875rem;
  }

  .btn-reason {
    width: 100%;
    text-align: center;
  }
}
</style>
